import * as React from 'react';

import {Nut_Settings_User} from 'nutshell-core/settings-constants';

import {InternalSurveyModal} from 'shells/modal/internal-survey-modal/internal-survey-modal';
import {WhatsNewBanner} from 'shells/whats-new/whats-new-banner';

import {useDismissibleUserSetting} from '../../util/hooks/use-dismissible-user-setting';

import {useGetSessionUser} from '../core/use-get-session-user';

import type {FeedbackSurvey} from './feedback-surveys';
import {useFeedbackSubmit} from './graphql/hooks/use-feedback-submit';
import {getDisplayProps} from './helpers';
import {LearnMoreWrapper} from './learn-more-wrapper';

type Props = {
    survey: FeedbackSurvey;
};

export function InternalFeedbackBanner(props: Props) {
    const [isSurveyOpen, setIsSurveyOpen] = React.useState<boolean>(false);
    const [manuallyDismissed, setManuallyDismissed] = React.useState<boolean>(false);

    const {user} = useGetSessionUser();
    const {feedbackSubmit} = useFeedbackSubmit();
    const {shouldShow, onDismiss} = useDismissibleUserSetting(
        Nut_Settings_User.SETTING_INTERNAL_FEEDBACK_DISMISSED_PREFIX + props.survey.id
    );

    const isAdmin = Boolean(user && user.isAdministrator);
    const displayProps = getDisplayProps(props.survey, isAdmin);

    const handleDismiss = React.useCallback(() => {
        setManuallyDismissed(true);
        if (onDismiss) {
            onDismiss();
        }
    }, [onDismiss]);

    const handleSubmit = React.useCallback(
        (response: string) => {
            return feedbackSubmit({
                key: props.survey.id,
                response,
                uri: window.location.href,
            });
        },
        [feedbackSubmit, props.survey.id]
    );

    const handleOpenSurvey = React.useCallback(() => {
        setIsSurveyOpen(true);
    }, []);

    if (!shouldShow || manuallyDismissed) {
        return null;
    }

    const learnMoreConfig = props.survey && props.survey.learnMore;

    const bannerProps = {
        message: displayProps.bannerTitle,
        feedbackLabel: displayProps.feedbackPrompt || undefined,
        onOpenSurvey: handleOpenSurvey,
        onHidden: handleDismiss,
        animate: true,
        lessPadding: true,
    } as const;

    return (
        <>
            {learnMoreConfig ? (
                <LearnMoreWrapper {...bannerProps} learnMoreConfig={learnMoreConfig} />
            ) : (
                <WhatsNewBanner {...bannerProps} />
            )}
            {isSurveyOpen && (
                <InternalSurveyModal
                    onClose={() => setIsSurveyOpen(false)}
                    title={displayProps.surveyTitle}
                    description={displayProps.surveySubtitle}
                    thumbnailIllustration={displayProps.surveyImage || undefined}
                    thumbnailBackgroundColor={displayProps.surveyImageBackgroundColor || undefined}
                    onSubmit={handleSubmit}
                />
            )}
        </>
    );
}
